import React from 'react';
import Link from 'components/link';
import Button from 'components/control/button';
import MapIcon from 'components/icon/map-icon';
import { trackEvent } from 'utils/google-tag-manager';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { useUserContext } from 'contexts';
import { homepageIds } from 'constants/test-constants';
import { GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON,
  GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON } from 'constants/events';
import replaceFrenchAccents from 'utils/replace-french-accents';
import dynamic from 'next/dynamic';
import { useIsMobile, useIsTablet } from 'hooks/use-size-class';
import { capitalizeLocation } from '@zoocasa/node-kit/strings/capitalize';

import type Listing from 'data/listing';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { OnListingCardClickProps } from 'utils/listing-card';

const ListingCardCarousel = dynamic(() => import('components/listing-card-carousel'));

type ZoocasaForSaleSectionProps = {
  className?: string;
  titleClassName?: string;
  isCrawler: boolean;
  availableHomeListings: ListingCardData[];
  availableHomeListingsLocation: string;
  isExpandedArea?: boolean;
  homesForSaleUrl: string;
  mapViewUrl: string;
  availableHomeListingsMeta: number;
  onListingCardClick: (props: OnListingCardClickProps) => void;
};

export default function ZoocasaForSaleSection({
  className,
  titleClassName,
  isCrawler,
  availableHomeListings,
  availableHomeListingsLocation,
  isExpandedArea,
  homesForSaleUrl,
  mapViewUrl,
  availableHomeListingsMeta,
  onListingCardClick,
}: ZoocasaForSaleSectionProps) {
  const { userLocation } = useUserContext();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const defaultSectionTitle = (
    <>
      {availableHomeListingsMeta}+ Properties{!isMobile && ' For Sale'} {isExpandedArea ? 'near' : 'in'}
    </>
  );

  return (
    <section className={buildClassName(styles.component, className)}>
      <div className={styles['home-block']} data-testid={homepageIds.homesForSaleWidget}>
        <div className={styles.header}>
          <div className={buildClassName(styles.left, titleClassName)} data-testid={homepageIds.homesForSaleTitle}>
            <p className={styles.properties}>
              {defaultSectionTitle}
            </p>

            <Link
              onClick={() => trackEvent(GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON)}
              href={replaceFrenchAccents(homesForSaleUrl)}
            >
              <h2 className={styles.location} data-testid={homepageIds.homesForSaleLocation}>{capitalizeLocation(availableHomeListingsLocation)}</h2>
            </Link>
          </div>
          {!isTablet &&
          <Link href={mapViewUrl}>
            <Button label="Map View" Icon={MapIcon} theme="secondary" testId={homepageIds.homesForSaleMapViewButton}
              onClick={()=>{ trackEvent(GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON); }} />
          </Link>
          }
        </div>
        <ListingCardCarousel listingCardsData={{ listings: availableHomeListings as Listing[], ctaType: 'available', userLocation, redirectUrl: replaceFrenchAccents(homesForSaleUrl) }} isCrawler={isCrawler} onListingCardClick={onListingCardClick} />
        {isTablet &&
          <div className={styles['mobile-view-map']}>
            <Link href={mapViewUrl}>
              <Button label="Map View" Icon={MapIcon} theme="secondary" testId={homepageIds.homesForSaleMapViewButton}
                onClick={()=>{
                  trackEvent(GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON);
                }} />
            </Link>
          </div>
        }
      </div>
    </section>
  );
}
